import { ref } from 'vue'
import type { Component, HTMLAttributes, VNode } from 'vue'
import type { ButtonVariants } from '@/components/ui/button'

export type ToastType = 'default' | 'success' | 'info' | 'warning' | 'error' | 'loading'

export type ToastId = string | number

export type ToastCloseReason = 'dismiss' | 'auto'

export interface ToastAction {
  label: string
  onClick: (event: MouseEvent) => void
  /** Button hierarchy (default: `link-color`). */
  hierarchy?: ButtonVariants['hierarchy']
  /** Button size (default: `sm`). */
  size?: ButtonVariants['size']
  /** Keep the toast open after the action is clicked (default: dismiss). */
  keepOpen?: boolean
}

export interface ToastOptions {
  /** Provide an id to update/dedupe an existing toast instead of creating a new one. */
  id?: ToastId
  description?: string
  /** Action buttons rendered below the body. */
  actions?: ToastAction[]
  /** Back-compat single action (sonner-style shape); normalized into `actions`. */
  action?: ToastAction
  /** Show a close (X) button. Falls back to the `<Toaster>` default. */
  closeButton?: boolean
  /**
   * Auto-dismiss delay in ms. Use `Infinity` to make the toast sticky.
   * Defaults to `Infinity` when the toast has actions, `4000` otherwise.
   */
  duration?: number
  /** Override the type icon by name. Pass an empty string to suppress the icon. */
  icon?: string
  /** Extra classes merged onto the toast's Alert shell. */
  class?: HTMLAttributes['class']
  /** Render a custom component / render function as the toast body. */
  component?: Component | (() => VNode)
  componentProps?: Record<string, unknown>
  onDismiss?: (id: ToastId) => void
  onAutoClose?: (id: ToastId) => void
}

/** The settled promise, carrying the toast's id so callers can still dismiss it. */
export type PromiseToast<T> = Promise<T> & { id: ToastId }

export interface PromiseToastOptions<T> {
  loading: string
  success: string | ((data: T) => string)
  error: string | ((error: unknown) => string)
  description?: string
  /** Duration applied once the promise settles (default: `DEFAULT_DURATION`). */
  duration?: number
}

/** Normalized entry stored in the reactive queue. */
export interface ToastEntry {
  id: ToastId
  type: ToastType
  message?: string
  description?: string
  icon?: string
  component?: Component | (() => VNode)
  componentProps?: Record<string, unknown>
  actions: ToastAction[]
  closeButton?: boolean
  duration: number
  class?: HTMLAttributes['class']
  onDismiss?: (id: ToastId) => void
  onAutoClose?: (id: ToastId) => void
}

export interface ToastFn {
  (message: string, options?: ToastOptions): ToastId
  success: (message: string, options?: ToastOptions) => ToastId
  info: (message: string, options?: ToastOptions) => ToastId
  warning: (message: string, options?: ToastOptions) => ToastId
  error: (message: string, options?: ToastOptions) => ToastId
  loading: (message: string, options?: ToastOptions) => ToastId
  custom: (component: Component | (() => VNode), options?: ToastOptions) => ToastId
  promise: <T>(
    promise: Promise<T> | (() => Promise<T>),
    options: PromiseToastOptions<T>
  ) => PromiseToast<T>
  dismiss: (id?: ToastId) => void
}

const DEFAULT_DURATION = 4000

let counter = 0

/** Skips ids already taken, so an autogenerated one never replaces a caller's toast. */
const genId = (): ToastId => {
  let id: ToastId
  do {
    id = `toast-${++counter}`
  } while (toasts.value.some((entry) => entry.id === id))
  return id
}

const ICON_BY_TYPE: Record<ToastType, string | undefined> = {
  default: undefined,
  success: 'check-circle',
  info: 'info-circle',
  warning: 'alert-triangle',
  error: 'x-circle',
  loading: undefined // rendered as a <Spinner> by Toast.vue
}

/**
 * Active toasts (newest pushed last), rendered by the single `<Toaster>` mounted
 * in `App.vue`. Module-level rather than a Pinia store: `toast()` is called from
 * plain event handlers and interceptors with no component instance, so it must
 * not depend on an active Pinia, and nothing outside `<Toaster>` reads the queue.
 */
export const toasts = ref<ToastEntry[]>([])

/**
 * Auto-dismiss timer for the front toast. It lives here rather than in `<Toast>`
 * so that a countdown is governed by the queue, not by whether a component
 * happens to be mounted.
 *
 * Exactly one toast — the newest, at the front of the stack — counts down at a
 * time. Everything behind it waits, then gets its own full duration once it
 * reaches the front. Arming every visible toast at once would give a burst a
 * single shared deadline, and the whole batch would vanish together.
 */
interface ToastTimer {
  /** `null` while paused (stack hovered/focused, or window blurred). */
  handle: ReturnType<typeof setTimeout> | null
  remaining: number
  startedAt: number
}

const timers = new Map<ToastId, ToastTimer>()
let timersPaused = false

function clearTimer(id: ToastId): void {
  const timer = timers.get(id)
  if (!timer) return
  if (timer.handle !== null) clearTimeout(timer.handle)
  timers.delete(id)
}

function armTimer(id: ToastId, timer: ToastTimer): void {
  timer.startedAt = Date.now()
  timer.handle = setTimeout(() => remove(id, 'auto'), timer.remaining)
}

/** Begin the countdown for `id`. Sticky (`Infinity`) durations never arm one. */
function startTimer(id: ToastId, duration: number): void {
  if (!Number.isFinite(duration)) return
  const timer: ToastTimer = { handle: null, remaining: duration, startedAt: Date.now() }
  timers.set(id, timer)
  if (!timersPaused) armTimer(id, timer)
}

/**
 * Hand the countdown to the front toast. Idempotent: a front toast already
 * counting down keeps its remaining time, so a queue change behind it never
 * extends its life.
 */
function syncTimers(): void {
  const front = toasts.value[toasts.value.length - 1]
  timers.forEach((_, id) => {
    if (id !== front?.id) clearTimer(id)
  })
  if (front && !timers.has(front.id)) startTimer(front.id, front.duration)
}

export function pauseTimers(): void {
  if (timersPaused) return
  timersPaused = true
  const now = Date.now()
  timers.forEach((timer) => {
    if (timer.handle === null) return
    clearTimeout(timer.handle)
    timer.handle = null
    timer.remaining = Math.max(0, timer.remaining - (now - timer.startedAt))
  })
}

export function resumeTimers(): void {
  if (!timersPaused) return
  timersPaused = false
  timers.forEach((timer, id) => armTimer(id, timer))
}

const normalizeActions = (options: ToastOptions): ToastEntry['actions'] => {
  if (options.actions?.length) return options.actions
  if (options.action) return [options.action]
  return []
}

const createEntry = (
  id: ToastId,
  type: ToastType,
  message: string | undefined,
  options: ToastOptions
): ToastEntry => {
  const actions = normalizeActions(options)
  return {
    id,
    type,
    message,
    description: options.description,
    icon: 'icon' in options ? options.icon || undefined : ICON_BY_TYPE[type],
    component: options.component,
    componentProps: options.componentProps,
    actions,
    closeButton: options.closeButton,
    // An actionable toast is a question, so it waits for an answer instead of
    // auto-closing. The close button still lets the user dismiss it.
    duration: options.duration ?? (actions.length ? Infinity : DEFAULT_DURATION),
    class: options.class,
    onDismiss: options.onDismiss,
    onAutoClose: options.onAutoClose
  }
}

/**
 * Add a toast, or — when `options.id` matches an existing one — replace it in
 * place. `<Toaster>` keys on the id, so the same DOM node morphs (e.g. promise
 * loading → success) without replaying the enter animation.
 */
function add(type: ToastType, message: string | undefined, options: ToastOptions = {}): ToastId {
  const id = options.id ?? genId()
  const entry = createEntry(id, type, message, options)
  const index = toasts.value.findIndex((t) => t.id === id)
  if (index >= 0) {
    toasts.value[index] = entry
    clearTimer(id) // a replaced toast restarts its countdown
  } else {
    toasts.value.push(entry)
  }
  syncTimers()
  return id
}

/** Replace an existing toast, or do nothing when it has already been dismissed. */
function update(id: ToastId, type: ToastType, message: string, options: ToastOptions): void {
  const index = toasts.value.findIndex((t) => t.id === id)
  if (index < 0) return
  toasts.value[index] = createEntry(id, type, message, options)
  clearTimer(id)
  syncTimers()
}

export function remove(id: ToastId, reason: ToastCloseReason = 'dismiss'): void {
  const index = toasts.value.findIndex((t) => t.id === id)
  if (index < 0) return
  const entry = toasts.value[index]
  clearTimer(id)
  toasts.value.splice(index, 1)
  // Promote whatever was queued behind the window before notifying the caller.
  syncTimers()
  if (reason === 'auto') entry.onAutoClose?.(id)
  else entry.onDismiss?.(id)
}

export function dismiss(id?: ToastId): void {
  if (id == null) {
    const entries = toasts.value
    toasts.value = []
    entries.forEach((t) => {
      clearTimer(t.id)
      t.onDismiss?.(t.id)
    })
    return
  }
  remove(id)
}

/** Ergonomic, import-anywhere API. */
export const toast: ToastFn = Object.assign(
  (message: string, options?: ToastOptions): ToastId => add('default', message, options),
  {
    success: (message: string, options?: ToastOptions) => add('success', message, options),
    info: (message: string, options?: ToastOptions) => add('info', message, options),
    warning: (message: string, options?: ToastOptions) => add('warning', message, options),
    error: (message: string, options?: ToastOptions) => add('error', message, options),
    loading: (message: string, options?: ToastOptions) =>
      add('loading', message, { ...options, duration: options?.duration ?? Infinity }),
    custom: (component: Component | (() => VNode), options?: ToastOptions) =>
      add('default', undefined, { ...options, component }),
    promise<T>(
      promise: Promise<T> | (() => Promise<T>),
      options: PromiseToastOptions<T>
    ): PromiseToast<T> {
      const pending = typeof promise === 'function' ? promise() : promise
      // No `closeButton` override: the toast keeps the same affordance before and
      // after settling, and a hung promise stays dismissable.
      const id = add('loading', options.loading, { duration: Infinity })
      // `update` no-ops when the user dismissed the loading toast mid-flight.
      const settle = (type: 'success' | 'error', message: string) =>
        update(id, type, message, { description: options.description, duration: options.duration })

      const settled = pending.then(
        (data) => {
          settle(
            'success',
            typeof options.success === 'function' ? options.success(data) : options.success
          )
          return data
        },
        (error) => {
          settle(
            'error',
            typeof options.error === 'function' ? options.error(error) : options.error
          )
          throw error
        }
      )
      // Keep the rejection available to the caller without it going unhandled
      // when they only wanted the toast.
      settled.catch(() => undefined)
      return Object.assign(settled, { id })
    },
    dismiss
  }
) as ToastFn
